// 型別化事件匯流排 — 取代 Unity CriminalMakers.GameEventHub。
// 解耦控制器與 UI：發布者 emit，訂閱者 on，回傳取消訂閱函式。

export type EventHandler<T> = (payload: T) => void

// E 為「事件名稱 → payload 型別」的介面（不需索引簽章）。
export class EventBus<E extends object> {
  private readonly handlers = new Map<keyof E, Set<EventHandler<unknown>>>()

  on<K extends keyof E>(type: K, handler: EventHandler<E[K]>): () => void {
    let set = this.handlers.get(type)
    if (set === undefined) {
      set = new Set()
      this.handlers.set(type, set)
    }
    set.add(handler as EventHandler<unknown>)
    return () => this.off(type, handler)
  }

  once<K extends keyof E>(type: K, handler: EventHandler<E[K]>): () => void {
    const off = this.on(type, (payload) => {
      off()
      handler(payload)
    })
    return off
  }

  off<K extends keyof E>(type: K, handler: EventHandler<E[K]>): void {
    this.handlers.get(type)?.delete(handler as EventHandler<unknown>)
  }

  emit<K extends keyof E>(type: K, payload: E[K]): void {
    const set = this.handlers.get(type)
    if (set === undefined) return
    // 複製一份以容許處理器在迭代中取消訂閱
    for (const handler of [...set]) {
      ;(handler as EventHandler<E[K]>)(payload)
    }
  }

  clear(): void {
    this.handlers.clear()
  }
}
